import { useState } from "react"
import _ from "lodash"
import { v4 as idGeneratorV4 } from "uuid"

//Formik
import { useField } from "formik"
import FormikTextInput from "../../../../../helpers/formikInputs/FormikTextInput"
import FormikCheckboxInput from "../../../../../helpers/formikInputs/FormikCheckboxInput"
import FormikRadioButtonInput from "../../../../../helpers/formikInputs/FormikRadioButtonInput"
import FormikDropDown from "../../../../../helpers/formikInputs/FormikDropDown"

//PrimeReact
import { Button } from "primereact/button"
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup"
import { Divider } from "primereact/divider"

//Styles
import myStyles from "../../../../../styles/assessments/ManagementFormBuilder.module.css"
import assessStyles from "../../../../../styles/assessments/Assessment.module.css"

//Assessments components
import elementTypes from "../Elements/elementTypes"
import QuestionOptions from "./QuestionOptions"
import QuestionDefaultValue from "./QuestionDefaultValue"
import ValidateInputRequired from "./ValidateInputRequired"
import QuestionMinMaxValue from "./QuestionMinMaxValue"
import QuestionDateOptions from "./QuestionDateOptions"
import ConditionalProperties from "./ConditionalProperties"
import QuestionOptionAlignment from "./QuestionOptionAlignment"
import QuestionHeight from "./QuestionHeight"
import QuestionText from "./QuestionText"
import { updateOrdering } from "../helpers/dndElementHelper"
import FormulaBuilder from "../FormulaBuilder/FormulaBuilder"

export default function FormBuilderProperties({
  questionOrders,
  questionOrderId,
  questionId,
  onClose,
  disabled,
}) {
  const [deletedOptionId, setDeletedOptionId] = useState(undefined)
  const [showFormulaBuilder, setShowFormulaBuilder] = useState(false)

  const questionOrderIndex = questionOrders.findIndex(
    (questionOrder) => questionOrder.id === questionOrderId
  )
  const questionIndex = questionId
    ? questionOrders[questionOrderIndex].questionGroup?.questions?.findIndex(
        (question) => question.id === questionId
      )
    : undefined

  let isEditingQuestion = true
  let elementType = ""
  let formikObjectName = `questions[${questionOrderIndex}]`

  if (questionOrders[questionOrderIndex].question) {
    formikObjectName = `${formikObjectName}.question`
    elementType = questionOrders[questionOrderIndex].question.type
  } else if (!questionId) {
    formikObjectName = `${formikObjectName}.questionGroup`
    elementType = elementTypes.groupElementId
    isEditingQuestion = false
  } else {
    formikObjectName = `${formikObjectName}.questionGroup.questions[${questionIndex}]`
    elementType =
      questionOrders[questionOrderIndex].questionGroup.questions[questionIndex]
        .type
  }

  const scoreFromAnswerFieldName = `${formikObjectName}.scoreFromAnswer`
  const scoreFormulaFieldName = `${formikObjectName}.scoreFormula`

  const [questionField, ,] = useField(formikObjectName)
  const [valueAsScoreField, ,] = useField(scoreFromAnswerFieldName)
  const [, , scoreFormulaFieldHelpers] = useField(scoreFormulaFieldName)

  const [questionOrder, , questionOrderHelpers] = useField(
    `questions[${questionOrderIndex}]`
  )
  const [questions, , questionsHelper] = useField("questions")
  const [, , conditionalRequiredHelpers] = useField(
    `${formikObjectName}.conditionalRequired`
  )

  const elementProfile = [
    ...elementTypes.basicElements,
    ...elementTypes.specialElements,
    ...elementTypes.hiddenTypes,
  ].find((element) => element.id === elementType)

  const cloneQuestionEntity = (newQuestion) => {
    //Reset the new question Id
    newQuestion.id = idGeneratorV4()
    newQuestion.isNew = true

    //Reset question options
    if (newQuestion.options) {
      newQuestion.options = newQuestion.options.map((option) => {
        return {
          ...option,
          id: `${idGeneratorV4()}_${option.text}`,
          isNew: true,
        }
      })
    }

    //Reset labels for matrixes
    if (newQuestion.questionLabels) {
      newQuestion.questionLabels = newQuestion.questionLabels.map((label) => {
        return {
          ...label,
          id: `${idGeneratorV4()}_${label.text}`,
          isNew: true,
        }
      })
    }

    if (newQuestion?.scoreFormula?.length > 0) {
      newQuestion.scoreFormula = cloneScoreFormula(newQuestion?.scoreFormula)
    }

    return newQuestion
  }

  const cloneScoreFormula = (scoreFormula) => {
    let newScoreFormula = []

    if (scoreFormula && scoreFormula.length > 0) {
      newScoreFormula = _.cloneDeep(scoreFormula)

      newScoreFormula.forEach((formula, index) => {
        formula.id = idGeneratorV4() + "_formula_" + index
        formula.isNew = true

        if (formula.question) {
          formula.question.id = idGeneratorV4() + "_question_" + index
          formula.question.isNew = true
        } else if (formula.value) {
          formula.value.id = idGeneratorV4() + "_value_" + index
          formula.value.isNew = true
        } else if (formula.function) {
          formula.function.id = idGeneratorV4() + "_function_" + index
          formula.function.isNew = true

          formula.function.values = cloneScoreFormula(formula.function.values)

          formula.function.values.forEach((value) => {
            value.functionId = formula.function.id
          })
        } else if (formula.group) {
          formula.group.id = idGeneratorV4() + "_group_" + index
          formula.group.isNew = true

          formula.group.formulas = cloneScoreFormula(formula.group.formulas)

          formula.group.formulas.forEach((groupFormula) => {
            groupFormula.groupId = formula.group.id
          })
        }
      })
    }

    return newScoreFormula
  }

  const cloneEntity = () => {
    const addNewOrderEntry = (orderEntry) => {
      //Add the new question at the end of the array
      let newQuestions = JSON.parse(JSON.stringify(questions.value))
      newQuestions = [...newQuestions, orderEntry]

      newQuestions = updateOrdering(newQuestions)

      questionsHelper.setValue(newQuestions)
    }

    if (questionOrder.value.question) {
      //Clone the top level question
      let newQuestionOrder = JSON.parse(JSON.stringify(questionOrder.value))
      newQuestionOrder.id = idGeneratorV4()
      newQuestionOrder.isNew = true

      newQuestionOrder.question = cloneQuestionEntity(newQuestionOrder.question)
      addNewOrderEntry(newQuestionOrder)
    } else if (questionOrder.value.questionGroup) {
      if (!questionId) {
        //Clone the entire group with its questions
        let newQuestionOrder = JSON.parse(JSON.stringify(questionOrder.value))
        newQuestionOrder.id = idGeneratorV4()
        newQuestionOrder.isNew = true

        newQuestionOrder.questionGroup.id = idGeneratorV4()
        newQuestionOrder.questionGroup.isNew = true

        newQuestionOrder.questionGroup.questions =
          newQuestionOrder.questionGroup.questions.map((question, index) => {
            let newQuestion = cloneQuestionEntity(question)
            newQuestion.id = newQuestion.id + "_" + index
            return newQuestion
          })

        if (newQuestionOrder.questionGroup?.scoreFormula?.length > 0) {
          newQuestionOrder.questionGroup.scoreFormula = cloneScoreFormula(
            newQuestionOrder.questionGroup.scoreFormula
          )
        }

        addNewOrderEntry(newQuestionOrder)
      } else {
        //Clone a question within a group
        let newQuestionOrder = JSON.parse(JSON.stringify(questionOrder.value))
        let newQuestion = JSON.parse(JSON.stringify(questionField.value))
        newQuestion = cloneQuestionEntity(newQuestion)

        newQuestion.groupOrder =
          newQuestionOrder.questionGroup.questions.length + 1

        //Add the new question clone to the group
        newQuestionOrder.questionGroup.questions = [
          ...newQuestionOrder.questionGroup.questions,
          newQuestion,
        ]

        newQuestionOrder.questionGroup.questions = updateOrdering(
          newQuestionOrder.questionGroup.questions
        )

        questionOrderHelpers.setValue(newQuestionOrder)
      }
    }
  }

  const FormulaBuilderButton = () => {
    return (
      <>
        <Divider />
        <div className={myStyles.duplicateElement}>
          <label>Formula</label>
          <Button
            type="button"
            onClick={(event) => {
              setShowFormulaBuilder(true)
            }}
            disabled={disabled || valueAsScoreField.value === true}
          >
            <span className={`material-icons`}>{"calculate"}</span>
            {isEditingQuestion
              ? "Edit question formula"
              : "Edit section formula"}
          </Button>
        </div>
      </>
    )
  }

  return (
    <div className={myStyles.propertiesContainer}>
      {showFormulaBuilder ? (
        <FormulaBuilder
          questionOptions={questionOrders}
          formikObjectName={scoreFormulaFieldName}
          parentFormikObjectName={formikObjectName}
          onClose={() => {
            setShowFormulaBuilder(false)
          }}
        />
      ) : null}
      <div className={myStyles.propertiesTitleLayout}>
        <div className={myStyles.propertiesTitleLayoutTitle}>
          Properties: {elementProfile?.label || "Unknown element type"}
        </div>
        <div className={myStyles.propertiesTitleLayoutClose}>
          <Button
            className={assessStyles.actionButton}
            type="button"
            onClick={onClose}
          >
            <span className={`material-icons`}>{"close"}</span>
          </Button>
        </div>
      </div>
      <ConfirmPopup />
      {isEditingQuestion ? (
        <>
          <QuestionText
            formikQuestionName={formikObjectName}
            questionType={elementProfile.id}
            disabled={disabled}
          />
          <ValidateInputRequired
            validList={elementTypes.propertiesConfig.hasHelperText}
            elementType={elementProfile.id}
          >
            <FormikTextInput
              name={`${formikObjectName}.helpText`}
              label={"Help text"}
              placeholderText={"Specify help text for this question"}
              disabled={disabled}
            />
          </ValidateInputRequired>
          <ValidateInputRequired
            validList={elementTypes.propertiesConfig.hasRequiredIndicator}
            elementType={elementProfile.id}
          >
            <div className={assessStyles.switchControl}>
              <FormikCheckboxInput
                name={`${formikObjectName}.required`}
                label={"Required"}
                useSwitch={true}
                onChangeCallback={(value) => {
                  if (questionField.value?.conditionalSource !== undefined) {
                    conditionalRequiredHelpers.setValue(value)
                  }
                }}
                disabled={disabled}
              />
            </div>
          </ValidateInputRequired>
          <ValidateInputRequired
            validList={elementTypes.propertiesConfig.hasWidth}
            elementType={elementProfile.id}
          >
            <div className={assessStyles.selectButton}>
              <FormikRadioButtonInput
                name={`${formikObjectName}.width`}
                label={"Width"}
                options={[
                  { label: "25%", value: "calc( 25% - 3px)" },
                  { label: "50%", value: "calc( 50% - 3px)" },
                  { label: "75%", value: "calc( 75% - 3px)" },
                  { label: "100%", value: "100%" },
                ]}
                optionLabel={"label"}
                optionValue={"value"}
                useSelectButton={true}
                disabled={disabled}
              />
            </div>
          </ValidateInputRequired>

          <ValidateInputRequired
            validList={elementTypes.propertiesConfig.hasHeight}
            elementType={elementProfile.id}
          >
            <QuestionHeight
              formikQuestionName={formikObjectName}
              questionType={elementProfile.id}
              disabled={disabled}
            />
          </ValidateInputRequired>

          <ValidateInputRequired
            validList={elementTypes.propertiesConfig.hasAlignment}
            elementType={elementProfile.id}
          >
            <div className={assessStyles.selectButton}>
              <QuestionOptionAlignment
                formikQuestionName={formikObjectName}
                disabled={disabled}
                questionType={elementProfile.id}
              />
            </div>
          </ValidateInputRequired>
          <ValidateInputRequired
            validList={[
              elementTypes.matrixCheckboxElementId,
              elementTypes.matrixElementId,
            ]}
            elementType={elementProfile.id}
          >
            <FormikRadioButtonInput
              name={`${formikObjectName}.type`}
              label={"Input type"}
              options={[
                {
                  label: "Multiple choice",
                  value: elementTypes.matrixCheckboxElementId,
                },
                { label: "Single choice", value: elementTypes.matrixElementId },
              ]}
              optionLabel={"label"}
              optionValue={"value"}
              disabled={disabled}
            />

            <QuestionOptions
              formikQuestionName={formikObjectName}
              onDelete={(optionId) => {
                setDeletedOptionId(optionId)
              }}
              questionType={elementProfile.id}
              isLabel={true}
              disabled={disabled}
            />
          </ValidateInputRequired>
          <ValidateInputRequired
            validList={elementTypes.propertiesConfig.hasOptions}
            elementType={elementProfile.id}
          >
            <QuestionOptions
              formikQuestionName={formikObjectName}
              onDelete={(optionId) => {
                setDeletedOptionId(optionId)
              }}
              questionType={elementProfile.id}
              disabled={disabled}
            />
          </ValidateInputRequired>
          <ValidateInputRequired
            validList={elementTypes.propertiesConfig.hasDefault}
            elementType={elementProfile.id}
          >
            <QuestionDefaultValue
              formikQuestionName={formikObjectName}
              questionType={elementProfile.id}
              deletedOptionId={deletedOptionId}
              onResetDeletedOptionId={() => {
                setDeletedOptionId(undefined)
              }}
              disabled={disabled}
            />
          </ValidateInputRequired>
          <ValidateInputRequired
            validList={[
              ...elementTypes.propertiesConfig.hasMinValue,
              ...elementTypes.propertiesConfig.hasMaxValue,
            ]}
            elementType={elementProfile.id}
          >
            <QuestionMinMaxValue
              formikQuestionName={formikObjectName}
              questionType={elementProfile.id}
              disabled={disabled}
            />
          </ValidateInputRequired>
          <ValidateInputRequired
            validList={[elementTypes.numberElementId]}
            elementType={elementProfile.id}
          >
            <div className={assessStyles.switchControl}>
              <FormikCheckboxInput
                name={scoreFromAnswerFieldName}
                label={"Use answer value as score"}
                useSwitch={true}
                onChangeCallback={(value) => {
                  if (questionField.value?.conditionalSource !== undefined) {
                    conditionalRequiredHelpers.setValue(value)
                  }

                  //Only trigger the following if the value is specifically true
                  if (value === true) {
                    scoreFormulaFieldHelpers.setValue([]) //Reset the formula if switching to value as score
                  }
                }}
                disabled={disabled}
              />
            </div>
          </ValidateInputRequired>
          <ValidateInputRequired
            validList={[elementTypes.dateElementId]}
            elementType={elementProfile.id}
          >
            <QuestionDateOptions
              formikQuestionName={formikObjectName}
              disabled={disabled}
            />
          </ValidateInputRequired>
          <ConditionalProperties
            formikQuestionName={formikObjectName}
            questionId={questionId}
            questionOrders={questionOrders}
            disabled={disabled}
          />
          {disabled ? null : (
            <div className={myStyles.duplicateElement}>
              <label>Duplicate field</label>
              <Button
                type="button"
                onClick={(event) => {
                  confirmPopup({
                    target: event.currentTarget,
                    message: "Are you sure you want to clone this question?",
                    icon: "pi pi-exclamation-triangle",
                    defaultFocus: "accept",
                    accept: () => {
                      cloneEntity()
                    },
                  })
                }}
                disabled={disabled}
              >
                <span className={`material-icons`}>{"content_copy"}</span>
                Duplicate
              </Button>
            </div>
          )}
          {elementProfile.allowFormula && <FormulaBuilderButton />}
        </>
      ) : (
        <>
          <FormikTextInput
            name={`${formikObjectName}.name`}
            label={"Name"}
            placeholderText={"Please specify a name for this group"}
            required={true}
            disabled={disabled}
          />
          <FormikDropDown
            label={"Alignment"}
            helpText={
              "Indicate how questions should be arranged on the assessment form on the preview or when completing the assessment"
            }
            name={`${formikObjectName}.alignment`}
            options={[
              { label: "Vertical", value: "vertical" },
              { label: "Horizontal", value: "horizontal" },
            ]}
            optionLabel={"label"}
            optionValue={"value"}
            disabled={disabled}
          />
          <div className={assessStyles.switchControl}>
            <FormikCheckboxInput
              name={`${formikObjectName}.expanded`}
              label={"Expanded"}
              helpText={
                "Indicates whether the section should be expanded by default"
              }
              useSwitch={true}
              disabled={disabled}
            />
          </div>
          <div className={myStyles.duplicateElement}>
            <label>Duplicate group</label>
            <Button
              type="button"
              onClick={(event) => {
                confirmPopup({
                  target: event.currentTarget,
                  message: "Are you sure you want to clone this group?",
                  icon: "pi pi-exclamation-triangle",
                  defaultFocus: "accept",
                  accept: () => {
                    cloneEntity()
                  },
                })
              }}
            >
              <span className={`material-icons`}>{"content_copy"}</span>
              Duplicate
            </Button>
          </div>
          <FormulaBuilderButton />
        </>
      )}
    </div>
  )
}
